// =================================================================
// CENTRALIZED COLOR CONFIGURATION
// =================================================================
// This file contains all color definitions for The Random Store
// Based on logo colors: #4abf18 (green) and #272735 (dark blue-gray)

// Logo Colors (Base)
export const LOGO_COLORS = {
  primary: '#4abf18',      // Logo green
  secondary: '#272735',    // Logo dark blue-gray
};

// =================================================================
// OPTION 1: SOPHISTICATED NEUTRAL (RECOMMENDED)
// Clean, professional, uses green sparingly for maximum impact
// =================================================================
export const THEME_SOPHISTICATED = {
  // Common colors (MUI compatibility + convenience)
  common: {
    white: '#ffffff',
    black: '#000000'
  },
  // Primary brand colors
  primary: {
    main: '#4abf18',       // Logo green - use for CTAs and important elements
    light: '#6dd149',      // Lighter green
    dark: '#3a9915',       // Darker green
    // Navy reads cleanly on bright green (~6:1). White-on-green fails WCAG AA.
    contrastText: '#272735',
    // Darkened green that passes AA on white — for green TEXT on light bg (prices, links).
    textOnLight: '#2a8011',
    // M3 Tonal Surface Wash
    container: '#eaf8e3',
    onContainer: '#113a04'
  },

  // Secondary colors
  secondary: {
    main: '#272735',       // Logo dark - navigation, headers
    light: '#3d3f52',      // Lighter version
    dark: '#1a1c26',       // Darker version
    contrastText: '#ffffff',
    container: '#edebf2',
    onContainer: '#181824'
  },

  // Material 3 Tonal Surfaces
  surface: {
    base: '#ffffff',
    dim: '#f4f6f3',
    containerLowest: '#ffffff',
    containerLow: '#f7f9f6',
    container: '#eef3ec',
    containerHigh: '#e5ede2',
    containerHighest: '#dbe5d8',
    glass: 'rgba(255, 255, 255, 0.85)'
  },

  // Material 3 Outlines
  outline: {
    main: '#74796e',
    variant: '#e2e5df',
    subtle: 'rgba(0, 0, 0, 0.08)'
  },
  
  // Background colors
  background: {
    default: '#f7f9f6',    // Soft sage-dimmed canvas for M3 feel
    paper: '#ffffff',      // Card/paper background
    light: '#f7f9f6',      // Light sections
    dark: '#eef3ec'        // Darker sections
  },
  
  // Text colors
  text: {
    primary: '#272735',    // Main text color
    secondary: '#5c606c',  // Secondary text
    disabled: '#9ea3af',   // Disabled text
    light: '#ffffff',      // White text on dark backgrounds
    hint: '#74b9ff'        // Hint text
  },
  
  // Functional colors
  success: {
    main: '#22a059',       // Success green
    light: '#3fd07e',
    dark: '#177840',
    container: '#e2f6eb'
  },
  
  error: {
    main: '#e63946',       // Error red
    light: '#ff6b77',
    dark: '#b31e2a',
    container: '#ffebee'
  },
  
  warning: {
    main: '#f59e0b',       // Warning orange
    light: '#fbbf24',
    dark: '#d97706',
    container: '#fef3c7'
  },
  
  info: {
    main: '#0288d1',       // Info blue
    light: '#4bacf0',
    dark: '#01579b',
    container: '#e1f5fe'
  },

  // Playful accent palette — used for category stickers, badges and
  // the "random & funky" energy. Green stays the hero; these add personality.
  accent: {
    lemon: '#ffd23f',      // sunny yellow
    coral: '#ff5d5d',      // warm pop
    sky: '#26c6f0',        // electric blue
    grape: '#9b5de5',      // playful purple
    tangerine: '#ff8a3d',  // orange
    mint: '#00d29e',       // fresh mint
    ink: '#272735'         // brand dark, for high-contrast funky blocks
  },

  // M3 Expressive Filament Accents (with container tints & accessible text)
  accentsM3: {
    lemon: { main: '#ffd23f', container: '#fff8db', onContainer: '#594500' },
    sky: { main: '#26c6f0', container: '#e3f8fd', onContainer: '#00475e' },
    coral: { main: '#ff5c5c', container: '#ffe9e9', onContainer: '#660d0d' },
    grape: { main: '#9b5de5', container: '#f4ebfd', onContainer: '#450e7a' },
    tangerine: { main: '#ff8a3d', container: '#fff0e5', onContainer: '#632700' },
    mint: { main: '#00d29e', container: '#e1faf3', onContainer: '#004a36' },
  },

  // Pure black from the brand swatch, for bold contrast sections/footer
  ink: '#15151c',

  // Border colors
  border: {
    light: '#eaece7',      // Soft M3 borders
    medium: '#d8ded3',     // Medium borders
    dark: '#a2ab9d',       // Dark borders
    glow: 'rgba(74, 191, 24, 0.28)'
  },

  // Luminescent shadow levels
  shadow: {
    level1: '0px 2px 8px -2px rgba(39, 39, 53, 0.05), 0px 1px 3px 0px rgba(39, 39, 53, 0.04)',
    level2: '0px 10px 24px -4px rgba(39, 39, 53, 0.08), 0px 4px 12px -2px rgba(74, 191, 24, 0.08)',
    level3: '0px 20px 40px -8px rgba(39, 39, 53, 0.16), 0px 8px 16px -4px rgba(39, 39, 53, 0.08)',
    buttonPrimary: '0px 6px 16px rgba(74, 191, 24, 0.28)',
    buttonHover: '0px 10px 26px rgba(74, 191, 24, 0.40)'
  },
  
  // Action colors
  action: {
    hover: 'rgba(74, 191, 24, 0.04)',     // Green hover
    selected: 'rgba(74, 191, 24, 0.08)',   // Green selected
    disabled: 'rgba(0, 0, 0, 0.26)',       // Disabled
    focus: 'rgba(74, 191, 24, 0.12)'       // Focus
  }
};

// =================================================================
// OPTION 2: MODERN DARK THEME
// For dark mode or modern dark aesthetic
// =================================================================
export const THEME_DARK = {
  common: {
    white: '#ffffff',
    black: '#000000'
  },
  primary: {
    main: '#4abf18',
    light: '#6dd149',
    dark: '#3a9915',
    contrastText: '#ffffff'
  },
  
  secondary: {
    main: '#272735',
    light: '#3d3f52',
    dark: '#1a1c26',
    contrastText: '#ffffff'
  },
  
  background: {
    default: '#1a1a1a',    // Very dark background
    paper: '#2d2d2d',      // Dark cards
    light: '#363636',      // Light sections
    dark: '#0f0f0f'        // Darker sections
  },
  
  text: {
    primary: '#ffffff',    // White text
    secondary: '#b0b0b0',  // Light gray text
    disabled: '#6b6b6b',   // Disabled text
    hint: '#1dd1a1'        // Hint text (teal)
  },
  
  accent: {
    teal: '#1dd1a1',       // Complementary teal
    purple: '#6c5ce7',     // Accent purple
    orange: '#fd79a8',     // Warm accent
    blue: '#74b9ff'        // Light blue
  },
  
  outline: {
    main: '#8e918f',
    variant: '#444746',
    subtle: 'rgba(255, 255, 255, 0.12)'
  }
};

// =================================================================
// OPTION 3: ULTRA MINIMAL
// Very clean, almost monochromatic with green accents
// =================================================================
export const THEME_MINIMAL = {
  common: {
    white: '#ffffff',
    black: '#000000'
  },
  primary: {
    main: '#4abf18',
    light: '#6dd149',
    dark: '#3a9915',
    contrastText: '#ffffff'
  },
  
  secondary: {
    main: '#272735',
    light: '#3d3f52',
    dark: '#1a1c26',
    contrastText: '#ffffff'
  },
  
  // Neutral gray scale
  neutral: {
    50: '#f9fafb',         // Lightest
    100: '#f3f4f6',        // Very light
    200: '#e5e7eb',        // Light borders
    300: '#d1d5db',        // Medium light
    400: '#9ca3af',        // Medium
    500: '#6b7280',        // Text gray
    600: '#4b5563',        // Dark text
    700: '#374151',        // Very dark
    800: '#1f2937',        // Almost black
    900: '#111827'         // Black
  },
  
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    light: '#f9fafb',      // neutral.50
    medium: '#f3f4f6',     // neutral.100
    dark: '#e5e7eb'        // neutral.200
  },
  
  text: {
    primary: '#1f2937',    // neutral.800
    secondary: '#4b5563',  // neutral.600
    disabled: '#9ca3af',   // neutral.400
    light: '#ffffff'
  }
};

// =================================================================
// EXPORT DEFAULT THEME (Change this to switch themes)
// =================================================================
export const COLORS = THEME_SOPHISTICATED; // <-- Change this line to switch themes

// =================================================================
// UTILITY FUNCTIONS
// =================================================================

// Function to get color with opacity
export const withOpacity = (color, opacity) => {
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Function to generate hover color (slightly darker)
export const getHoverColor = (color) => {
  // Simple darkening - in a real app you might want a more sophisticated approach
  return color.replace('#', '#').concat('dd'); // Adds transparency
};

// Gradient helpers
export const GRADIENTS = {
  primary: `linear-gradient(135deg, ${COLORS.primary.light}, ${COLORS.primary.main})`,
  secondary: `linear-gradient(135deg, ${COLORS.secondary.light}, ${COLORS.secondary.main})`,
  success: `linear-gradient(135deg, ${COLORS.success.light}, ${COLORS.success.main})`,
  subtle: `linear-gradient(135deg, ${COLORS.background.light}, ${COLORS.background.default})`,
  // Funky brand gradient for hero / dark blocks
  funky: `linear-gradient(135deg, ${COLORS.secondary.main} 0%, ${COLORS.secondary.dark} 100%)`
};

// Rotating accent colors for the "random & funky" look (category tiles, stickers)
export const FUN_STICKERS = [
  COLORS.primary.main,     // brand green
  COLORS.accent.lemon,
  COLORS.accent.coral,
  COLORS.accent.sky,
  COLORS.accent.grape,
  COLORS.accent.tangerine
];

// Deterministically pick a funky accent for a label/category so colors stay
// stable across renders but feel pleasantly random across the catalog.
export const stickerColor = (key = '') => {
  const str = String(key);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return FUN_STICKERS[Math.abs(hash) % FUN_STICKERS.length];
};

// --- WCAG contrast helpers -------------------------------------------------
const channelToLinear = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

const relativeLuminance = (hex) => {
  const h = String(hex).replace('#', '');
  const r = channelToLinear(parseInt(h.substring(0, 2), 16));
  const g = channelToLinear(parseInt(h.substring(2, 4), 16));
  const b = channelToLinear(parseInt(h.substring(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (hexA, hexB) => {
  const lighter = Math.max(relativeLuminance(hexA), relativeLuminance(hexB));
  const darker = Math.min(relativeLuminance(hexA), relativeLuminance(hexB));
  return (lighter + 0.05) / (darker + 0.05);
};

// Pick navy or white text for a sticker background so the label stays legible:
// light accents (lemon/sky/tangerine/coral/green) get navy, the darker grape gets white.
export const stickerText = (bg) =>
  contrastRatio(bg, '#ffffff') >= contrastRatio(bg, COLORS.secondary.main)
    ? '#ffffff'
    : COLORS.secondary.main;

// M3 Expressive sticker badge helper: returns matched container, text, and border
export const stickerBadge = (key = '') => {
  const str = String(key);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  const keys = ['primary', 'lemon', 'sky', 'coral', 'grape', 'tangerine', 'mint'];
  const picked = keys[Math.abs(hash) % keys.length];
  if (picked === 'primary') {
    return {
      bg: COLORS.primary.container,
      text: COLORS.primary.onContainer,
      border: COLORS.primary.main,
      main: COLORS.primary.main
    };
  }
  const acc = COLORS.accentsM3[picked];
  return {
    bg: acc.container,
    text: acc.onContainer,
    border: acc.main,
    main: acc.main
  };
};

export default COLORS;
